import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { CtaButton } from '@/components/ui/CtaButton';
import { ROUTES } from '@/constants/routes';
import logoUrl from '@/assets/icons/logo.svg?url';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: 16px;
  max-width: 430px;
  margin: 0 auto;
`;

const Logo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  margin-bottom: 16px;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 0 0 16px 0;
  font-weight: 700;
  font-size: 42px;
  line-height: 110%;
  letter-spacing: -0.5px;
  color: #1c1c1e;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 16px 0;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: 0.2px;
  color: #6b7280;
  text-align: center;
  font-weight: 400;
`;

const Description = styled.div`
  max-width: 100%;
  margin: 0 0 32px 0;
  padding: 24px;
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
`;

const DescriptionText = styled.p`
  margin: 0 0 16px 0;
  font-size: 16px;
  line-height: 160%;
  color: #374151;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Features = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  margin-bottom: 32px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #54c7fa;
    box-shadow: 0 4px 12px rgba(84, 199, 250, 0.1);
  }
`;

const FeatureIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const FeatureText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #1c1c1e;
  letter-spacing: 0.2px;
`;

const CtaWrapper = styled.div`
  width: 100%;
`;

export function HomePage() {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate(ROUTES.AUTH.LOGIN);
  };

  return (
    <Container>
      <Logo>
        <LogoImage src={logoUrl} alt="Shieldix" />
      </Logo>

      <Title>Shieldix</Title>
      <Subtitle>VPN & AI Protection</Subtitle>

      <Description>
        <DescriptionText>
          Secure your digital life with next-gen VPN powered by AI. Shieldix combines ultra-fast encrypted connections with intelligent threat detection — for privacy, safety, and freedom online.
        </DescriptionText>
        <DescriptionText>
          Unlock the full power of Shieldix: by subscribing on our website, you'll receive download links for our iOS app and get instant Premium access within the app. Enjoy seamless protection, advanced features, and priority performance — anytime, anywhere.
        </DescriptionText>
      </Description>

      <Features>
        <FeatureItem>
          <FeatureIcon>🔒</FeatureIcon>
          <FeatureText>Military-grade encryption</FeatureText>
        </FeatureItem>
        <FeatureItem>
          <FeatureIcon>🤖</FeatureIcon>
          <FeatureText>AI-powered threat detection</FeatureText>
        </FeatureItem>
        <FeatureItem>
          <FeatureIcon>⚡</FeatureIcon>
          <FeatureText>Lightning-fast speeds</FeatureText>
        </FeatureItem>
      </Features>

      <CtaWrapper>
        <CtaButton onClick={handleGetStarted} showArrow>
          Log in
        </CtaButton>
      </CtaWrapper>
    </Container>
  );
}
